import {
  Box,
  Button,
  Grid,
  Hidden,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Paper,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AddIcon from '@mui/icons-material/Add';
import { Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import UserContext from '../../shared-components/contexts/UserContext';
import GroupListItem, {
  GroupListItemSkeleton,
} from './Components/GroupListItem';
import IGroup from './Interfaces/IGroup';
import GroupService from './Services/GroupService';

const useStyles = makeStyles({
  listItem: {
    borderBottom: '1px solid silver',
  },
  listHeading: {
    fontWeight: 600,
  },
  subtitle2: {
    fontStyle: 'italic',
    color: '#666',
  },
  content: {
    backgroundColor: 'white',
    padding: '30px',
    boxShadow: '2px 2px 0 rgba(0,0,0,.1)',
    borderLeft: '1px solid #e0e0e0',
    borderTop: '1px solid #e0e0e0',
  },
});

const Groups = () => {
  const PAGE_SIZE = 50;

  const classes = useStyles();
  const user = useContext(UserContext);

  const { enqueueSnackbar } = useSnackbar();

  const [initialLoad, setInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const [groups, setGroups] = useState<IGroup[]>([]);
  const [totalGroups, setTotalGroups] = useState(0);

  useEffect(() => {
    getGroups(1)
      .then(g => {
        setGroups(g);
      })
      .finally(() => {
        setInitialLoad(false);
      });
  }, []);

  const getGroups = async (page?: number) => {
    return GroupService.getGroups({
      page: page,
      pageSize: PAGE_SIZE,
      searchParameters: {
        createdByUserId: user.id,
      },
    }).then(g => {
      setTotalGroups(g.totalItems);
      return g.items;
    });
  };

  return (
    <>
      <Box display="flex">
        <Box sx={{ flexGrow: 1 }}>
          <Box>
            <Typography variant="h1">Groups</Typography>
          </Box>
        </Box>
        <Box>
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            style={{ marginTop: '10px' }}
            onClick={() => {
              window.location.replace('/beacon/students/group/create');
            }}
          >
            <Hidden smDown>Create Group</Hidden>
            <Hidden smUp>Create</Hidden>
          </Button>
        </Box>
      </Box>

      <Paper className={classes.content}>
        <Grid container spacing={4}>
          <Grid item sm={12} md={12} lg={12}>
            <List component="div">
              <ListSubheader className={classes.listItem} component="div">
                <Box display="flex">
                  <Box flexGrow={1}>
                    <Typography className={classes.listHeading} variant="h2">
                      Name
                    </Typography>
                  </Box>
                  <Box className={classes.listHeading}>
                    {initialLoad ? (
                      <Skeleton width={115} />
                    ) : (
                      <Typography>
                        {`Showing ${groups.length.toLocaleString()} of ${totalGroups}`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </ListSubheader>
              {initialLoad ? (
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    position: 'relative',
                  }}
                >
                  {[...Array(PAGE_SIZE)].map((e, i) => (
                    <GroupListItemSkeleton key={i} />
                  ))}
                </ul>
              ) : (
                <InfiniteScroll
                  element="ul"
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    position: 'relative',
                  }}
                  useWindow={true}
                  initialLoad={false}
                  hasMore={groups.length < totalGroups && !isLoading}
                  threshold={500}
                  loadMore={page => {
                    setIsLoading(true);
                    getGroups(page + 1)
                      .then(g => setGroups([...groups, ...g]))
                      .catch(e => {
                        enqueueSnackbar(
                          'Failed to load groups. Please refresh this page and try again later',
                          {
                            variant: 'error',
                          },
                        );
                      })
                      .finally(() => {
                        setIsLoading(false);
                      });
                  }}
                >
                  {groups.map(g => (
                    <GroupListItem key={g.id} group={g} />
                  ))}
                </InfiniteScroll>
              )}
            </List>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default Groups;
